// =============================================================================
// useStaticSite — wiersz static_sites po mapie (skrot w kroku „Migracja") ALBO
// po website_id (Studio /strony/:websiteId). Realtime na wierszu + mutacje
// ustawien + lista redirectow.
// =============================================================================

import { useCallback, useEffect, useState } from "react";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import type { StaticRedirectRow, StaticSiteRow, StaticSiteSettings, ThemeId } from "@/services/staticSite/types";
import {
  addRedirect, ensureStaticSite, getStaticSiteByMap, getStaticSiteByWebsite, listRedirects, removeRedirect, updateSettings, updateStaticSite,
} from "@/services/staticSite/migrationRunner";

export interface StaticSiteKey {
  mapId?: string;
  websiteId?: string;
}

export function useStaticSite(key: StaticSiteKey) {
  const { mapId, websiteId } = key;
  const [site, setSite] = useState<StaticSiteRow | null>(null);
  const [redirects, setRedirects] = useState<StaticRedirectRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!mapId && !websiteId) return;
    setLoading(true);
    try {
      const row = websiteId ? await getStaticSiteByWebsite(websiteId) : await getStaticSiteByMap(mapId!);
      setSite(row);
      setError(null);
      if (row) setRedirects(await listRedirects(row.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [mapId, websiteId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const rtKey = websiteId ? `website_id=eq.${websiteId}` : mapId ? `map_id=eq.${mapId}` : undefined;
  useRealtimeSubscription({
    channel: `static_site_${websiteId || mapId || "none"}`,
    table: "static_sites",
    filter: rtKey,
    enabled: Boolean(rtKey),
    onChange: () => { void refresh(); },
  });

  const ensure = useCallback(
    async (input: { websiteId: string; websiteUrl: string; workspace?: "firma" | "sideprojects" | null }) => {
      if (!mapId) throw new Error("Brak mapId");
      const row = await ensureStaticSite({ mapId, ...input });
      setSite(row);
      return row;
    },
    [mapId],
  );

  const saveSettings = useCallback(
    async (patch: Partial<StaticSiteSettings>) => {
      if (!site) return;
      const next = await updateSettings(site, patch);
      setSite({ ...site, settings: next });
    },
    [site],
  );

  const setTheme = useCallback(
    async (themeId: ThemeId) => {
      if (!site) return;
      await updateStaticSite(site.id, { theme_id: themeId });
      setSite({ ...site, theme_id: themeId });
    },
    [site],
  );

  const patchSite = useCallback(
    async (patch: Partial<StaticSiteRow>) => {
      if (!site) return;
      await updateStaticSite(site.id, patch);
      setSite({ ...site, ...patch });
    },
    [site],
  );

  const addRedirectRow = useCallback(
    async (row: Omit<StaticRedirectRow, "id" | "site_id">) => {
      if (!site) return;
      await addRedirect(site.id, row);
      setRedirects(await listRedirects(site.id));
    },
    [site],
  );

  const removeRedirectRow = useCallback(
    async (id: string) => {
      if (!site) return;
      await removeRedirect(id);
      setRedirects(await listRedirects(site.id));
    },
    [site],
  );

  return {
    site, redirects, loading, error, refresh, ensure, saveSettings, setTheme, patchSite,
    addRedirect: addRedirectRow, removeRedirect: removeRedirectRow, setSite,
  };
}
